import { Component, Input, Output, EventEmitter, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';

/**
 * ReplaceTemplateModalComponent - Modal dialog used to confirm replacing a template.
 * @component
 * @selector app-replace-template-modal
 * @example
 *   <app-replace-template-modal [isOpen]="true" [isLoading]="isSaving" (close)="onModalClose()" (confirm)="onConfirmReplace()"></app-replace-template-modal>
 * @input boolean isOpen - Controls whether the modal is visible. When true the modal is shown.
 * @input boolean isLoading - Indicates an in-progress operation; when true, closing actions are prevented to avoid interrupting the process.
 * @output EventEmitter<void> close - Emitted when the modal is requested to close (will not emit if isLoading is true).
 * @output EventEmitter<void> confirm - Emitted when the user confirms the replace action.
 * @method onClose(): void - Attempts to close the modal; checks isLoading and emits close when allowed.
 * @method onConfirm(): void - Emits the confirm event to signal that the user confirmed the replacement.
 * @method onOverlayClick(event: MouseEvent): void - Handles clicks on the overlay backdrop; if the click target is the overlay and not loading, triggers onClose to close the modal.
 * @lifecycle No Angular lifecycle hooks (ngOnInit, ngOnDestroy, etc.) are implemented; component behavior is driven by inputs and the event handlers above.
 */
@Component({
  selector: 'app-replace-template-modal',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './replace-template-modal.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./replace-template-modal.component.scss'],
})
export class ReplaceTemplateModalComponent {
  @Input() isOpen = false;
  @Input() isLoading = false;
  @Output() close = new EventEmitter<void>();
  @Output() confirm = new EventEmitter<void>();

  onClose(): void {
    // Prevent closing when loading
    if (this.isLoading) {
      return;
    }
    this.close.emit();
  }

  onConfirm(): void {
    this.confirm.emit();
  }

  onOverlayClick(event: MouseEvent): void {
    // Prevent closing when loading
    if (this.isLoading) {
      return;
    }
    // Close modal when clicking on overlay (but not on the modal content)
    if ((event.target as HTMLElement).classList.contains('modal-overlay')) {
      this.onClose();
    }
  }
}
